import { Link } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import mockupAsset from "@/assets/nav/chris-heidlebaugh-colorado-web-design-mockup.webp.asset.json";
import auditAsset from "@/assets/nav/chris-heidlebaugh-colorado-digital-marketing-audit.webp.asset.json";
const mockupImg = mockupAsset.url;
const auditImg = auditAsset.url;
import logo from "@/assets/colorado-web-impressions-logo-mark-colorado-springs.webp";

// Mega menu: grouped columns on desktop, grouped accordion on mobile.
// Services point at STATEWIDE hubs so any Colorado visitor can self-select.
// Groups with mobileHidden are desktop-only (kept out of the mobile accordion).
type NavItem = { to: string; label: string };
type NavGroup = { title: string; items: ReadonlyArray<NavItem>; mobileHidden?: boolean };

type NavCta = { to: string; title: string; text: string; button: string; image: string; alt: string; pricingHash: string; pricingLabel: string };

const webDesignGroups: NavGroup[] = [
  {
    title: "Websites By Type",
    items: [
      { to: "/colorado-web-design", label: "Business Websites" },
      { to: "/ecommerce-seo-colorado", label: "Ecommerce Websites" },
      { to: "/franchise-marketing-colorado", label: "Franchise Websites" },
      { to: "/startup-bundle", label: "Startups & New Businesses" },
      { to: "/nonprofit-website-design-colorado", label: "Nonprofit Websites" },
    ],
  },
  {
    title: "Learn & Compare",
    mobileHidden: true,
    items: [
      { to: "/web-design", label: "Website Design Services" },
      { to: "/learn/website-redesign-services-colorado", label: "Website Redesigns" },
      { to: "/case-studies", label: "Portfolio" },
      { to: "/pricing", label: "Website Pricing" },
    ],
  },
];

const leadGenGroups: NavGroup[] = [
  {
    title: "Lead Generation",
    items: [
      { to: "/colorado-seo", label: "Colorado SEO" },
      { to: "/local-seo", label: "Google Maps / Local SEO" },
      { to: "/geo-ai", label: "AI & GEO Search" },
      { to: "/google-ads-ppc", label: "PPC & Google Ads" },
      { to: "/social-media-marketing-small-business-colorado", label: "Social Media" },
      { to: "/digital-marketing", label: "Digital Marketing" },
    ],
  },
];

const serviceGroups: NavGroup[] = [
  {
    title: "Business Services",
    items: [
      { to: "/crm-setup-colorado", label: "CRM Setup & Follow-Up" },
      { to: "/business-photography-videography-colorado", label: "Photography & Video" },
      { to: "/marketing-consulting-colorado", label: "Marketing Consulting" },
      { to: "/marketing-plans", label: "Marketing Plans" },
    ],
  },
  {
    title: "Audits",
    items: [
      { to: "/free-audit", label: "Free Marketing Audit" },
      { to: "/ai-visibility-audit", label: "AI Visibility Audit" },
      { to: "/colorado-springs-citation-audit", label: "Citation Audit" },
    ],
  },
];

const aboutGroups: NavGroup[] = [
  {
    title: "About",
    items: [
      { to: "/meet-chris", label: "Meet Chris" },
      { to: "/reviews", label: "Reviews" },
      { to: "/guarantees", label: "Guarantees" },
      { to: "/faq", label: "FAQ" },
      { to: "/learn", label: "Learn Center" },
      { to: "/answers", label: "Answers" },
    ],
  },
  {
    title: "Industries",
    items: [
      { to: "/hvac-marketing-colorado", label: "HVAC" },
      { to: "/plumbing-marketing-colorado", label: "Plumbing" },
      { to: "/roofing-marketing-colorado", label: "Roofing" },
      { to: "/landscaping-marketing-colorado", label: "Landscaping" },
      { to: "/law-firm-seo-colorado", label: "Law Firms" },
      { to: "/dental-marketing-colorado", label: "Dental" },
      { to: "/industries", label: "All Industries" },
    ],
  },
  {
    title: "Service Areas",
    items: [
      { to: "/colorado-springs-office-woodmen-rd", label: "Colorado Springs HQ" },
      { to: "/denver-digital-marketing", label: "Denver" },
      { to: "/castle-rock-digital-marketing", label: "Castle Rock" },
      { to: "/pueblo-digital-marketing", label: "Pueblo" },
      { to: "/fort-collins-digital-marketing", label: "Fort Collins" },
      { to: "/service-areas", label: "All Colorado Areas" },
    ],
  },
];

type DropdownKey = "web" | "leads" | "services" | "about";

const dropdowns: { key: DropdownKey; label: string; groups: NavGroup[]; width: string; cta?: NavCta }[] = [
  { key: "web", label: "Web Design", groups: webDesignGroups, width: "w-[46rem]", cta: { to: "/free-mockup", title: "Free Website Mockup", text: "See a custom homepage design for your business before you spend a dime.", button: "Get My Free Mockup", image: mockupImg, alt: "Chris Heidlebaugh, Colorado web design expert, reviewing a free website mockup on a laptop", pricingHash: "website-pricing", pricingLabel: "See Website Packages — From $1,850" } },
  { key: "leads", label: "Lead Generation", groups: leadGenGroups, width: "w-[36rem]", cta: { to: "/free-audit", title: "Free Digital Strategy Audit", text: "Find out what is blocking calls from Google, Maps and AI search.", button: "Get My Free Audit", image: auditImg, alt: "Chris Heidlebaugh, Colorado digital marketing coach, offering a free SEO and digital strategy audit", pricingHash: "lead-generation", pricingLabel: "See Lead Gen Plans — From $950/mo" } },
  { key: "services", label: "Services", groups: serviceGroups, width: "w-[30rem]" },
  { key: "about", label: "About", groups: aboutGroups, width: "w-[40rem]" },
];

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<DropdownKey | null>(null);
  const [mobileOpen, setMobileOpen] = useState<DropdownKey | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/95 backdrop-blur-lg">
      <div className="mx-auto flex h-20 w-full max-w-7xl items-center gap-2 px-4 sm:gap-3 sm:px-6 lg:gap-6 lg:px-8">
        {/* Logo — far left on every breakpoint */}
        <Link
          to="/"
          className="relative z-20 flex shrink-0 items-center leading-none"
          onClick={() => setOpen(false)}
        >
          <span className="-mb-8 inline-flex items-center justify-center rounded-b-2xl bg-brand-black px-1.5 pb-1.5 pt-1 shadow-elegant ring-1 ring-border/40 sm:-mb-10 sm:px-2 sm:pb-2 sm:pt-1.5 lg:-mb-14 lg:px-2.5 lg:pb-2.5 lg:pt-2">
            <img
              src={logo}
              alt="Colorado Web Impressions logo — Colorado Springs web design, SEO, and digital marketing agency"
              width={528}
              height={288}
              decoding="async"
              // @ts-expect-error — fetchpriority is a valid HTML attr not yet typed
              fetchpriority="high"
              data-geo-lat="38.9359976"
              data-geo-lng="-104.7653118"
              data-geo-city="Colorado Springs"
              className="block h-12 w-auto object-contain sm:h-14 lg:h-20"
            />
          </span>
        </Link>

        {/* Mobile: spacer pushes CTAs to the right */}
        <div className="flex-1 lg:hidden" />

        {/* Left: Nav (desktop only) */}
        <nav ref={wrapRef} className="hidden flex-1 items-center gap-1 lg:flex">
          {dropdowns.map((dd, idx) => (
            <div key={dd.key} className="relative flex items-center">
                <button
                  type="button"
                  onClick={() => setOpenDropdown((v) => (v === dd.key ? null : dd.key))}
                  className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-2 text-sm font-medium text-foreground/80 transition-smooth hover:bg-muted hover:text-foreground"
                  aria-expanded={openDropdown === dd.key}
                >
                  {dd.label}
                  <ChevronDown className={`h-4 w-4 transition-transform ${openDropdown === dd.key ? "rotate-180" : ""}`} />
                </button>
              {openDropdown === dd.key && (
                <div
                  className={`absolute top-full z-30 mt-2 ${dd.width} max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-background p-4 shadow-elegant ${idx === 0 ? "left-0" : idx === 1 ? "left-1/2 -translate-x-1/2" : "right-0"}`}
                >
                  <div className={dd.cta ? "flex gap-5" : ""}>
                  <div className="flex flex-1 flex-col">
                  <div
                    className="grid gap-x-6 gap-y-2"
                    style={{ gridTemplateColumns: `repeat(${dd.groups.length}, minmax(0, 1fr))` }}
                  >
                    {dd.groups.map((g) => (
                      <div key={g.title}>
                        <p className="mb-1 px-2 text-xs font-bold uppercase tracking-wider text-brand-gold-deep">{g.title}</p>
                        {g.items.map((item) => (
                          <Link
                            key={item.to + item.label}
                            to={item.to}
                            onClick={() => setOpenDropdown(null)}
                            className="block rounded-md px-2 py-2 text-sm font-medium text-foreground/80 transition-smooth hover:bg-muted hover:text-foreground"
                            activeProps={{ className: "text-foreground bg-muted" }}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                  {dd.cta && (
                    <Link
                      to="/pricing"
                      hash={dd.cta.pricingHash}
                      onClick={() => setOpenDropdown(null)}
                      className="mt-auto rounded-md border border-brand-gold-deep/40 bg-muted px-4 py-3 text-center text-sm font-bold text-foreground transition-smooth hover:bg-brand-gold-deep/10"
                    >
                      {dd.cta.pricingLabel} →
                    </Link>
                  )}
                  </div>
                  {dd.cta && (
                    <div className="flex w-60 shrink-0 flex-col gap-2">
                    <Link
                      to={dd.cta.to}
                      onClick={() => setOpenDropdown(null)}
                      className="group flex flex-col overflow-hidden rounded-lg border border-border bg-brand-black text-left shadow-elegant"
                    >
                      <img src={dd.cta.image} alt={dd.cta.alt} width={960} height={640} loading="lazy" className="aspect-[3/2] w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                      <span className="flex flex-1 flex-col gap-2 p-4">
                        <span className="text-base font-bold text-background">{dd.cta.title}</span>
                        <span className="text-xs leading-relaxed text-background/75">{dd.cta.text}</span>
                        <span className="mt-auto inline-flex justify-center rounded-md bg-gradient-gold px-3 py-2 text-xs font-bold uppercase tracking-wider text-brand-black">{dd.cta.button}</span>
                      </span>
                    </Link>
                    </div>
                  )}
                  </div>
                </div>
              )}
            </div>
          ))}
          <Link
            to="/pricing"
            onClick={() => setOpenDropdown(null)}
            className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md px-2.5 py-2 text-sm font-medium text-foreground/80 transition-smooth hover:bg-muted hover:text-foreground"
            activeProps={{ className: "text-foreground bg-muted" }}
          >
            Pricing
          </Link>
        </nav>

        {/* (Logo moved to far left above) */}

        {/* Right: CTA cluster */}
        <div className="flex shrink-0 items-center gap-2 sm:gap-3 lg:ml-auto lg:gap-4">
          {/* Desktop: phone with number */}
          <a
            href="tel:+17194193935"
            className="hidden items-center gap-2 whitespace-nowrap rounded-md px-2 py-2 text-sm font-semibold text-foreground transition-smooth hover:text-brand-gold-deep xl:inline-flex"
           aria-label="Call (719) 419-3935">
            <Phone className="h-4 w-4" />
            (719) 419-3935
          </a>

          {/* Mobile: phone icon-only click-to-call */}
          <a
            href="tel:+17194193935"
            aria-label="Call (719) 419-3935"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-brand-gold-deep transition-smooth hover:text-foreground xl:hidden"
          >
            <Phone className="h-6 w-6" strokeWidth={2.25} />
          </a>

          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-brand-black shadow-gold transition-smooth hover:-translate-y-0.5 sm:px-5 sm:py-2.5 sm:text-xs whitespace-nowrap lg:px-5 lg:py-3 lg:text-sm"
          >
            Get Proposal
          </Link>

          <button
            type="button"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center text-brand-gold-deep transition-smooth hover:text-foreground lg:hidden"
          >
            {open ? <X className="h-7 w-7" strokeWidth={2.5} /> : <Menu className="h-7 w-7" strokeWidth={2.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="absolute inset-x-0 top-full z-50 max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-border bg-background shadow-elegant lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col px-5 py-4">
            {dropdowns.map((dd) => (
              <div key={dd.key}>
                <button
                  type="button"
                  onClick={() => setMobileOpen((v) => (v === dd.key ? null : dd.key))}
                  className="flex w-full items-center justify-between rounded-md px-3 py-3 text-base font-medium text-foreground/80 hover:bg-muted hover:text-foreground"
                >
                  {dd.label}
                  <ChevronDown className={`h-4 w-4 transition-transform ${mobileOpen === dd.key ? "rotate-180" : ""}`} />
                </button>
                {mobileOpen === dd.key && (
                  <div className="ml-3 flex flex-col border-l border-border pl-3">
                    {dd.groups.filter((g) => !g.mobileHidden).map((g) => (
                      <div key={g.title} className="py-1">
                        {dd.groups.filter((g) => !g.mobileHidden).length > 1 && (
                          <p className="px-3 pt-2 text-xs font-bold uppercase tracking-wider text-brand-gold-deep">{g.title}</p>
                        )}
                        {g.items.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={() => setOpen(false)}
                            className="block rounded-md px-3 py-2.5 text-sm font-medium text-foreground/80 hover:bg-muted hover:text-foreground"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    ))}
                    {dd.cta && (
                      <Link to={dd.cta.to} onClick={() => setOpen(false)} className="my-2 ml-3 rounded-md bg-gradient-gold px-4 py-2.5 text-center text-sm font-bold text-brand-black">
                        {dd.cta.button}
                      </Link>
                    )}
                    {dd.cta && (
                      <Link to="/pricing" hash={dd.cta.pricingHash} onClick={() => setOpen(false)} className="mb-2 ml-3 block rounded-md border border-brand-gold-deep/40 px-4 py-2.5 text-center text-sm font-bold text-foreground">
                        {dd.cta.pricingLabel} →
                      </Link>
                    )}
                  </div>
                )}
              </div>
            ))}
            <Link to="/pricing" onClick={() => setOpen(false)} className="rounded-md px-3 py-3 text-base font-medium text-foreground/80 hover:bg-muted hover:text-foreground">
              Pricing
            </Link>
            <a
              href="tel:+17194193935"
              className="mt-2 inline-flex items-center gap-2 rounded-md px-3 py-3 text-base font-semibold text-foreground"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" /> (719) 419-3935
            </a>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-md bg-secondary px-5 py-3 text-center text-base font-semibold text-secondary-foreground"
            >
              Get a Quote
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
